import { useState, ReactElement } from "react";
import { MultiStepFormButton, IMultiStepFormButtonPartial } from "./MultiStepFormButton"

export interface ITab {
  title? : string
  text : string
  content? : ReactElement
  isDisabled? : boolean
  isSelected? : boolean
}

function Tab(
    { 
      tab, 
      index, 
      handleActiveTab, 
      activeTabIndex, 
      disableClick 
    } : 
    { 
      tab: ITab, 
      index: number, 
      handleActiveTab: Function, 
      activeTabIndex: number, 
      disableClick?: boolean 
    }
  ): JSX.Element {
  
  const isSelectedTab: boolean = (activeTabIndex === index)
  const isClickDisabled: boolean | undefined = (disableClick || tab.isDisabled)

  // tab button styles
  const tabButtonStyle = "inline-block py-2 px-4 w-full h-full"
  const clickableTabButtonStyle = "active:bg-gray-300 active:bg-none"
  const isSelectedTabButtonStyle = "bg-white"
  const unselectedTabButtonStyle = "border-t border-gray-200 bg-gray-50 hover:bg-gray-100"
  const isDisabledOrDisableClickTabButtonStyle = "hover:from-gray hover:to-white cursor-default"
  // tab title and text styles
  const tabTitleAndTextStyle = "leading-tight font-light"
  const tabTitleStyle = "text-xs text-gray-400"
  const tabTitleIsDisabledStyle = "text-xs text-gray-300"
  const tabTextStyle = "text-black-400"
  const tabTextIsDisabledStyle = "text-gray-300"
  
  return (
    <button 
      className={`${tabButtonStyle} 
        ${  isSelectedTab ? isSelectedTabButtonStyle : unselectedTabButtonStyle }
        ${ (disableClick || tab.isDisabled) ? isDisabledOrDisableClickTabButtonStyle : clickableTabButtonStyle}
      `}
      type="button" 
      role="tab" 
      onClick={() => !isClickDisabled && handleActiveTab(index)}
    >
      <div className={tabTitleAndTextStyle}>
        <span className={`${ !tab.isDisabled ? tabTitleStyle : tabTitleIsDisabledStyle}`}> 
          {tab.title} 
        </span>
        <br/>
        <span className={`${ !tab.isDisabled ? tabTextStyle : tabTextIsDisabledStyle}`}> 
          {tab.text} 
        </span>
      </div>
    </button>
  );
}

function getValidTabs(tabs: ITab[]) : ITab[] {
  return tabs.filter(tab => !tab.isDisabled)
}

function getSelectedTabIndex(validTabs: ITab[], tabs: ITab[]) : number {
  let selectedTabIndex = validTabs.findIndex(tab => tab.isSelected)
  if (selectedTabIndex === -1) {
    // Gets the first valid index of the tabs to leave as selected.
    selectedTabIndex = tabs.findIndex(tab => !tab.isDisabled)
  } 
  return selectedTabIndex;
}

function getValidTabIndexes (tabs: ITab[], validTabs: ITab[]) : number[] {
  let validIndexes : number[] = [];
  for (let i = 0; i < tabs.length; i++) {
    if (validTabs.includes(tabs[i])) {
      validIndexes.push(i);
    }
  }
  return validIndexes;
}

export function Tabs(
  { tabs, disableClick, useMultiStepButton, multiStepButtonProps } : 
  { tabs: ITab[], disableClick?: boolean, useMultiStepButton?: boolean, multiStepButtonProps?: IMultiStepFormButtonPartial }): 
  JSX.Element 
  {
  const validTabs = getValidTabs(tabs)
  const selectedTabIndex = getSelectedTabIndex(validTabs, tabs)
  const isSelectedTabIndexInvalid: boolean = (selectedTabIndex === -1)
  const [activeTabIndex, setActiveTabIndex] = useState(selectedTabIndex)
  const handleActiveTab = (selectedTabIndex: number) => {
    setActiveTabIndex(selectedTabIndex)
  };

  const tabLiStyle = "border w-1/6 max-w-xs overflow-ellipsis overflow-hidden border-r-0 hover:shadow"
  const firstTabLiStyle = "rounded-tl"
  const lastTabLiStyle = " rounded-tr border-r" 
  const disabledTabLiStyle = "hover:shadow-none"
  const isFirstTab = (index) => index === 0
  const isLastTab = (index) => index === tabs.length - 1

  return (  
    <div>
      <div 
        className="mb-5 border-b border-gray-200 dark:border-gray-700"
      >
        <ul 
          className="flex flex-wrap -mb-px text-sm font-medium text-center" 
          role="tablist"
        >
          { tabs
            .map((tab, index) => (
              <li 
                className={`${tabLiStyle}
                  ${ isFirstTab(index) && firstTabLiStyle }
                  ${ isLastTab(index) && lastTabLiStyle }
                  ${ tab.isDisabled && disabledTabLiStyle }
                `}
                role="presentation"
                key={index}
              >
                <Tab 
                  tab={tab}
                  index={index}
                  handleActiveTab={handleActiveTab}
                  activeTabIndex={activeTabIndex}
                  disableClick={disableClick}
                />
              </li>
          ))}
        </ul>
      </div>
      { !isSelectedTabIndexInvalid && 
        <div>
          {/* Generates the content associated with the tab passed through the arguments. */}
          {tabs[activeTabIndex].content}

          { useMultiStepButton &&
            /* Generates a button to manipulate the tabs. */
            <MultiStepFormButton
              props={{
                ...multiStepButtonProps, 
                currentStepIndex: activeTabIndex, 
                setCurrentStepIndex: setActiveTabIndex, 
                stepsIndexesArray: getValidTabIndexes(tabs, validTabs),
              }}
            />
          }
        </div>
      }
    </div>
  );
}
